import React, { useState, useEffect, useCallback } from 'react';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ReportsPage from 'src/pages/reports';
import * as reportsFitApi from 'src/apis/reportFit';
import * as reportsInactivationApi from 'src/apis/reportInactivation';
import * as etlApi from 'src/apis/etl';

/**
 * ReportsContainer component - Reports service layer to call API functions and send fetched data to main page component.
 * @returns {JSX.Element}
 * @example
 * <ReportsContainer />
 */
function ReportsContainer() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [etl, setEtl] = useState('');
  const activeTab = location.pathname.includes('/reports/inactivation') ? 'inactivation' : 'fit';

  const [fitData, setFitData] = useState([]);
  const [fitPagination, setFitPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0, limit: 10 });
  const [fitFilters, setFitFilters] = useState({});
  const [fitFilterOptions, setFitFilterOptions] = useState({});
  const [fitFetching, setFitFetching] = useState(false);
  const [fitExporting, setFitExporting] = useState(false);
  const [fitSort, setFitSort] = useState({});

  const [inactivationData, setInactivationData] = useState([]);
  const [inactivationPagination, setInactivationPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0, limit: 10 });
  const [inactivationFilters, setInactivationFilters] = useState({});
  const [inactivationFilterOptions, setInactivationFilterOptions] = useState({});
  const [inactivationFetching, setInactivationFetching] = useState(false);
  const [inactivationExporting, setInactivationExporting] = useState(false);
  const [inactivationSort, setInactivationSort] = useState({});

  /**
   * Get current ETL update datetime.
   */
  const getEtlDate = useCallback(async () => {
    try {
      const response = await etlApi.getETL();
      const { data } = response;
      const { result } = data;
      if (result.updateTimetable) setEtl(result.updateTimetable);
    } catch {
      setEtl('');
      notification.error({ message: t('errors.reports.getData') });
    }
  }, [t]);

  /**
   * Get paginated Fit History records.
   * @param {Object} [filters]
   * @param {Object} [pagination]
   */
  const fetchFitHistory = useCallback(async (filters = {}, pagination = { currentPage: 1, limit: 10 }, sort = {}) => {
    try {
      setFitFetching(true);
      const response = await reportsFitApi.list(filters, pagination, sort, i18n.language);
      const { data, pagination: pag } = response.data.result;
      setFitData(data);
      setFitPagination(pag);
    } catch {
      notification.error({ message: t('errors.reports.getData') });
    } finally {
      setFitFetching(false);
    }
  }, [t]);

  /**
   * Get paginated Inactive Profiles records.
   * @param {Object} [filters]
   * @param {Object} [pagination]
   */
  const fetchInactivation = useCallback(async (filters = {}, pagination = { currentPage: 1, limit: 10 }, sort = {}) => {
    try {
      setInactivationFetching(true);
      const response = await reportsInactivationApi.list(filters, pagination, sort);
      const { data, pagination: pag } = response.data.result;
      setInactivationData(data);
      setInactivationPagination(pag);
    } catch {
      notification.error({ message: t('errors.reports.getData') });
    } finally {
      setInactivationFetching(false);
    }
  }, [t]);

  /**
   * Get filter options for the active tab.
   */
  const getFitFilterOptions = useCallback(async () => {
    try {
      const response = await reportsFitApi.getFilters();
      setFitFilterOptions(response.data.result);
    } catch {
      notification.error({ message: t('errors.reports.getData') });
    }
  }, [t]);

  /**
   * Get filter options for Inactive Profiles.
   */
  const getInactivationFilterOptions = useCallback(async () => {
    try {
      const response = await reportsInactivationApi.getFilters();
      setInactivationFilterOptions(response.data.result);
    } catch {
      notification.error({ message: t('errors.reports.getData') });
    }
  }, [t]);

  useEffect(() => {
    getEtlDate();
  }, [getEtlDate]);

  useEffect(() => {
    if (activeTab === 'fit') {
      getFitFilterOptions();
      fetchFitHistory();
    }
    if (activeTab === 'inactivation') {
      getInactivationFilterOptions();
      fetchInactivation();
    }
  }, [activeTab, getFitFilterOptions, fetchFitHistory, getInactivationFilterOptions, fetchInactivation]);

  /**
   * Handle filter change for Fit History.
   * @param {Object} newFilters
   */
  const handleFitFilterChange = useCallback((newFilters) => {
    setFitFilters(newFilters);
    fetchFitHistory(newFilters, { currentPage: 1, limit: fitPagination.limit });
  }, [fetchFitHistory, fitPagination.limit]);

  /**
   * Handle filter change for Inactive Profiles.
   * @param {Object} newFilters
   */
  const handleInactivationFilterChange = useCallback((newFilters) => {
    setInactivationFilters(newFilters);
    fetchInactivation(newFilters, { currentPage: 1, limit: inactivationPagination.limit });
  }, [fetchInactivation, inactivationPagination.limit]);

  /**
   * Handle pagination change for Fit History.
   * @param {number} page
   * @param {number} limit
   */
  const handleFitPaginationChange = useCallback((page, limit) => {
    fetchFitHistory(fitFilters, { currentPage: page, limit }, fitSort);
  }, [fitFilters, fetchFitHistory, fitSort]);

  const handleFitSortChange = useCallback((sort) => {
    setFitSort(sort);
    fetchFitHistory(fitFilters, { currentPage: 1, limit: fitPagination.limit }, sort);
  }, [fitFilters, fitPagination.limit, fetchFitHistory]);

  /**
   * Handle pagination change for Inactive Profiles.
   * @param {number} page
   * @param {number} limit
   */
  const handleInactivationPaginationChange = useCallback((page, limit) => {
    fetchInactivation(inactivationFilters, { currentPage: page, limit }, inactivationSort);
  }, [inactivationFilters, fetchInactivation, inactivationSort]);

  const handleInactivationSortChange = useCallback((sort) => {
    setInactivationSort(sort);
    fetchInactivation(inactivationFilters, { currentPage: 1, limit: inactivationPagination.limit }, sort);
  }, [inactivationFilters, inactivationPagination.limit, fetchInactivation]);

  /**
   * Save blob data to file, using File System Access API when available.
   * Falls back to standard download link if not supported.
   * @param {Blob} blob - File data.
   * @param {string} filename - Suggested file name.
   * @param {string} mimeType - MIME type of the file.
   */
  const getSaveFileHandle = useCallback(async (filename, mimeType) => {
    if (!window.showSaveFilePicker) return null;
    const ext = filename.split('.').pop();
    return window.showSaveFilePicker({
      suggestedName: filename,
      types: [{ accept: { [mimeType]: [`.${ext}`] } }],
    });
  }, []);

  const saveFile = useCallback(async (blob, filename, fileHandle) => {
    if (fileHandle) {
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      notification.success({ message: t('reports.downloadSuccess') });
    } else {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      link.click();
      URL.revokeObjectURL(url);
    }
  }, [t]);

  const isUserCancellation = (err) => err?.name === 'AbortError';

  /**
   * Build export filename with timestamp.
   * @param {string} nameKey - i18n key for the report name.
   * @param {string} format - File format extension.
   * @returns {string}
   */
  const buildFileName = useCallback((nameKey, format) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const name = t(nameKey).replace(/\s+/g, '_');
    return `${name}_${timestamp}.${format}`;
  }, [t]);


  /**
   * Export Fit History records.
   * @param {string} format - Export format: 'csv', 'xlsx' or 'pdf'.
   */
  const handleExportFit = useCallback(async (format) => {
    try {
      const mimeTypes = { csv: 'text/csv', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', pdf: 'application/pdf' };
      const filename = buildFileName('reportsTitle.fit', format);
      const fileHandle = await getSaveFileHandle(filename, mimeTypes[format] || 'application/octet-stream');
      setFitExporting(true);
      const response = await reportsFitApi.exportReport(format, i18n.language || 'en');
      await saveFile(new Blob([response.data]), filename, fileHandle);
    } catch (err) {
      if (!isUserCancellation(err)) notification.error({ message: t('errors.reports.getData') });
    } finally {
      setFitExporting(false);
    }
  }, [t, buildFileName, saveFile, getSaveFileHandle]);

  /**
   * Export Inactive Profiles records.
   * @param {string} format - Export format: 'csv', 'xlsx' or 'pdf'.
   */
  const handleExportInactivation = useCallback(async (format) => {
    try {
      const mimeTypes = { csv: 'text/csv', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', pdf: 'application/pdf' };
      const filename = buildFileName('reportsTitle.inactivation', format);
      const fileHandle = await getSaveFileHandle(filename, mimeTypes[format] || 'application/octet-stream');
      setInactivationExporting(true);
      const response = await reportsInactivationApi.exportReport(format, i18n.language || 'en');
      await saveFile(new Blob([response.data]), filename, fileHandle);
    } catch (err) {
      if (!isUserCancellation(err)) notification.error({ message: t('errors.reports.getData') });
    } finally {
      setInactivationExporting(false);
    }
  }, [t, buildFileName, saveFile, getSaveFileHandle]);

  return (
    <ReportsPage
      etlDate={etl}
      fitData={fitData}
      fitPagination={fitPagination}
      fitFilterOptions={fitFilterOptions}
      fitFetching={fitFetching}
      onFitFilterChange={handleFitFilterChange}
      onFitPaginationChange={handleFitPaginationChange}
      onFitSortChange={handleFitSortChange}
      onExportFit={handleExportFit}
      fitExporting={fitExporting}
      inactivationData={inactivationData}
      inactivationPagination={inactivationPagination}
      inactivationFilterOptions={inactivationFilterOptions}
      inactivationFetching={inactivationFetching}
      onInactivationFilterChange={handleInactivationFilterChange}
      onInactivationPaginationChange={handleInactivationPaginationChange}
      onInactivationSortChange={handleInactivationSortChange}
      onExportInactivation={handleExportInactivation}
      inactivationExporting={inactivationExporting}
    />
  );
}

export default ReportsContainer;
